import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaskIcon } from '../components/Icon';
import { TimePicker } from '../components/TimePicker';
import { CalendarPopup } from '../components/CalendarPopup';
import { useBranding, useFeatureFlag } from '@alterrego/module-branding';
import { apiFetch } from '../utils/api';
import type { Agenda, AgendaService } from '@alterrego/module-agenda';
import { getOpeningForDay, getSpecialForDate, getSlotsForDay } from '@alterrego/module-agenda';
import './ReserverScreen.css';

const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const DAYS_FULL_FR = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
// Plafond self-service aligné sur le backend (SELF_SERVICE_MAX_COVERS = 10) et
// sur le chat Maïa. Au-delà → privatisation (contact direct du restaurant).
const MAX_GUESTS = 10;

type ServiceKey = 'midi' | 'soir';

interface ServiceWindow {
  key: ServiceKey;
  label: string;
  times: string[]; // 'HH:MM'
}

interface Suggestion {
  date: string;
  heure: string;
  service: ServiceKey;
  remaining: number;
  kind: 'slot' | 'autre_service' | 'lendemain';
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseYmd(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function serviceKeyForTime(hhmm: string): ServiceKey {
  return parseInt(hhmm, 10) < 15 ? 'midi' : 'soir';
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function fmtTime(hhmm: string): string {
  const [h, m] = hhmm.split(':');
  return `${parseInt(h, 10)}h${m}`;
}

// ── Clavier : garder le champ focus au-dessus du clavier ──
// /reserver est une route fullscreen : le scroll est porté par .fullscreen-content,
// dont la hauteur (100dvh) NE rétrécit PAS quand le clavier iOS monte — le bas du
// conteneur passe simplement DERRIÈRE le clavier. D'où deux exigences, sans
// lesquelles seuls les champs déjà en bas du contenu (le textarea) remontaient,
// les champs du milieu (Nom, Téléphone, Email) restant sous le clavier :
//   1. de la slack de scroll en bas (--keyboard-inset, cf. .rsv-step-confirm),
//      sinon le conteneur bute sur son scroll max et le champ ne PEUT pas monter ;
//   2. jouer le scroll quand le clavier est RÉELLEMENT monté, et le rejouer quand
//      sa hauteur change (pavé numérique du Téléphone ≠ clavier texte du Nom) —
//      un délai fixe deviné tombe à côté selon le champ et le device.
const KEYBOARD_MIN_HEIGHT = 100;

/**
 * Remonte le champ focus en HAUT (block:'start') du conteneur de scroll.
 * `onlyIfLow` : ne corrige que si le champ n'est pas déjà remonté — évite de
 * relancer un scroll smooth par-dessus un autre (les deux s'annulent).
 */
function scrollFocusedFieldToTop(onlyIfLow = false) {
  const el = document.activeElement as HTMLElement | null;
  if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return;
  const container = el.closest('.fullscreen-content');
  if (onlyIfLow && container && el.getBoundingClientRect().top - container.getBoundingClientRect().top < 120) return;
  el.scrollIntoView({ block: 'start', behavior: 'smooth' });
}

/**
 * onFocus commun à TOUS les champs de l'étape 2 (Nom, Téléphone, Email, textarea).
 * Deux passes, parce que le moment où le champ PEUT remonter dépend de la plateforme :
 *  - ~80ms : le clavier est déjà ouvert (passage Nom → Téléphone) ; laisse aussi
 *    React recommitter le CTA en flux avant de mesurer ;
 *  - ~340ms : en natif Capacitor le plugin clavier redimensionne le body ~300ms
 *    après le focus, et comme innerHeight rétrécit avec lui le listener
 *    visualViewport ne verra aucun delta → personne d'autre ne rejouerait le scroll.
 * Sur le web c'est le listener visualViewport qui fait le vrai travail, dès que le
 * clavier est monté et sa hauteur connue.
 */
function onFieldFocus() {
  setTimeout(() => scrollFocusedFieldToTop(), 80);
  setTimeout(() => scrollFocusedFieldToTop(true), 340);
}

/** Génère les créneaux 'HH:MM' d'un service (inclusif), au pas `interval`. */
function buildSlotTimes(first: string, last: string, interval: number): string[] {
  const out: string[] = [];
  const step = interval > 0 ? interval : 15;
  for (let t = toMinutes(first); t <= toMinutes(last); t += step) {
    out.push(`${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`);
  }
  return out;
}

/** Format téléphone FR au fil de la frappe : "06 12 34 56 78". */
function formatFrPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Fenêtres de service par défaut (agenda non chargé / pas de booking config).
const FALLBACK_WINDOWS: ServiceWindow[] = [
  { key: 'midi', label: 'Déjeuner', times: buildSlotTimes('12:00', '13:30', 15) },
  { key: 'soir', label: 'Dîner', times: buildSlotTimes('19:00', '21:30', 15) },
];

function windowsForDay(agenda: Agenda | null, day: Date): ServiceWindow[] {
  if (!agenda) return FALLBACK_WINDOWS;
  const slots = getSlotsForDay(agenda, day.getDay()).filter(
    (s: AgendaService) => s.booking?.enabled && s.recurrence?.start,
  );
  if (slots.length === 0) return FALLBACK_WINDOWS;
  const windows = slots.map((s: AgendaService): ServiceWindow => {
    const start = s.booking?.firstSlot ?? s.recurrence.start;
    const end = s.booking?.lastSlot ?? s.recurrence.end;
    const interval = s.booking?.interval ?? 15;
    return {
      key: serviceKeyForTime(start),
      label: s.label,
      times: buildSlotTimes(start, end, interval),
    };
  });
  return windows.sort((a, b) => (a.times[0] ?? '').localeCompare(b.times[0] ?? ''));
}

export function ReserverScreen() {
  const navigate = useNavigate();
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  // Page dédiée, règles simples : on rend tout de suite avec les fallbacks
  // (FALLBACK_WINDOWS, 14 jours depuis aujourd'hui, heures [12,13,19,20,21]).
  // L'agenda arrive en async et affine en place, sans skeleton ni transition.
  const [agenda, setAgenda] = useState<Agenda | null>(null);
  // Idem HomeScreen : sans agenda tenant, on garde les fallbacks (FALLBACK_WINDOWS)
  // sans tenter un appel qui répond 404.
  const hasAgenda = useFeatureFlag('agenda');

  useEffect(() => {
    if (!hasAgenda) return;
    apiFetch(`/reservations/agenda.json`, { auth: false })
      .then((r) => (r.ok ? r.json() : null))
      .then((a) => { if (a) setAgenda(a); })
      .catch(() => { /* fallback UI reste fonctionnel ; le POST gère la dispo réelle */ });
  }, [hasAgenda]);

  // ── Strip de dates STABLE : ancrée sur today, jamais réordonnée. ──
  // selectedDate est un état indépendant — sélectionner une puce change juste
  // son état visuel, sans la déplacer en tête (bug corrigé).
  // AUCUNE présélection : selectedDate démarre à null (aucun chip .active au
  // mount). L'agenda async ne doit PAS auto-sélectionner — l'user reste maître.
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);

  // Date utilisée pour CALCULER les options (fenêtres/heures du picker) tant que
  // l'user n'a rien choisi — n'implique aucune sélection visuelle.
  const pickerDate = selectedDate ?? today;

  const dateOptions = useMemo(() => {
    const dates: Date[] = [];
    let scanned = 0;
    while (dates.length < 21 && scanned < 90) {
      const d = new Date(today);
      d.setDate(d.getDate() + scanned);
      scanned++;
      if (agenda) {
        const opening = getOpeningForDay(agenda, d.getDay());
        if (!opening) continue;
        const special = getSpecialForDate(agenda, ymd(d));
        if (special?.type === 'closure') continue;
      }
      dates.push(d);
    }
    if (dates.length === 0) dates.push(today);
    // Garantit que selectedDate (ex: choisie au calendrier, hors fenêtre) est présente.
    if (selectedDate && !dates.some((d) => ymd(d) === ymd(selectedDate))) {
      dates.push(selectedDate);
      dates.sort((a, b) => a.getTime() - b.getTime());
    }
    return dates;
  }, [today, agenda, selectedDate]);

  const windows = useMemo(() => windowsForDay(agenda, pickerDate), [agenda, pickerDate]);

  // ── Heure ──
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  // Défaut intelligent : 1er créneau à venir (aujourd'hui) sinon 1er créneau.
  const defaultTime = useMemo(() => {
    const isToday = ymd(pickerDate) === ymd(today);
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    for (const w of windows) {
      for (const t of w.times) {
        if (!isToday || toMinutes(t) > nowMin) return t;
      }
    }
    return windows[0]?.times[0] ?? null;
  }, [windows, selectedDate, today]);

  const activeTime = selectedTime ?? defaultTime;
  const activeService: ServiceKey | null = activeTime ? serviceKeyForTime(activeTime) : null;

  // Heures + minutes disponibles dérivées des créneaux réellement servis (agenda
  // booking.interval / plages midi-soir). Alimente le TimePicker (selects natifs
  // = wheel iOS) : les heures et les minutes proposées sont déjà filtrées, donc
  // toute combinaison choisie est valide.
  const availableHours = useMemo(() => {
    const hs = new Set<number>();
    for (const w of windows) for (const t of w.times) hs.add(parseInt(t, 10));
    return [...hs].sort((a, b) => a - b);
  }, [windows]);

  const minutesByHour = useMemo(() => {
    const m: Record<number, number[]> = {};
    for (const w of windows) for (const t of w.times) {
      const [h, mm] = t.split(':').map(Number);
      (m[h] ??= []).push(mm);
    }
    return m;
  }, [windows]);

  const maxMinuteByHour = useMemo(() => {
    const r: Record<number, number> = {};
    for (const h of Object.keys(minutesByHour)) r[+h] = Math.max(...minutesByHour[+h]);
    return r;
  }, [minutesByHour]);

  const minuteStep = useMemo(() => {
    for (const w of windows) if (w.times.length >= 2) return toMinutes(w.times[1]) - toMinutes(w.times[0]);
    return 10;
  }, [windows]);

  const displayHour = activeTime ? parseInt(activeTime, 10) : availableHours[0] ?? 12;
  const displayMinute = activeTime ? parseInt(activeTime.split(':')[1], 10) : (minutesByHour[displayHour]?.[0] ?? 0);

  const onHourChange = useCallback((h: number) => {
    const mins = minutesByHour[h] ?? [0];
    const mm = mins.includes(displayMinute) ? displayMinute : mins[0];
    onSelectTime(`${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`);
  }, [minutesByHour, displayMinute]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMinuteChange = useCallback((mm: number) => {
    onSelectTime(`${String(displayHour).padStart(2, '0')}:${String(mm).padStart(2, '0')}`);
  }, [displayHour]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Couverts ── AUCUNE présélection : démarre à null (aucun chip .active).
  const [guests, setGuests] = useState<number | null>(null);

  // ── Pré-remplissage depuis l'URL (chat Maïa → « Ouvrir le formulaire »). ──
  // Additif et tolérant : ne s'exécute qu'au mount, ignore toute valeur invalide.
  // N'altère pas le comportement « aucune présélection » sans paramètres.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const qDate = sp.get('date');
    const qTime = sp.get('time');
    const qGuests = sp.get('guests');
    if (qDate && /^\d{4}-\d{2}-\d{2}$/.test(qDate)) {
      const d = parseYmd(qDate);
      if (!Number.isNaN(d.getTime())) setSelectedDate(d);
    }
    if (qTime && /^\d{1,2}:\d{2}$/.test(qTime)) setSelectedTime(qTime);
    if (qGuests) {
      const g = parseInt(qGuests, 10);
      if (g >= 1 && g <= MAX_GUESTS) setGuests(g);
    }
  }, []);

  // ── Disponibilité ── AUCUN check de capacité à l'étape 1 (pas de prefetch, pas
  // de chips grisés). Le résultat (dispo / complet + alternatives) est calculé au
  // click « Vérifier » et affiché à l'étape 2. availResult null = pas encore vérifié.
  const [availResult, setAvailResult] = useState<{ available: boolean; remaining: number | null; suggestions: Suggestion[] } | null>(null);

  // ── Contact ──
  const savedUser = useMemo(() => {
    try { const raw = localStorage.getItem('alterrego_user'); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  }, []);
  const [name, setName] = useState<string>(savedUser?.name ?? '');
  const [phone, setPhone] = useState<string>(savedUser?.phone ? formatFrPhone(savedUser.phone) : '');
  const [email, setEmail] = useState<string>(savedUser?.email ?? '');
  const [notes, setNotes] = useState('');
  // Pot de miel anti-bot : champ masqué qu'un humain ne remplit jamais. S'il
  // arrive non vide côté backend, la résa est ignorée (cf. routes/reservations).
  const [hp, setHp] = useState('');

  const nameValid = name.trim().length > 0;
  const phoneValid = phone.trim().length > 0;
  const emailValid = email.trim() === '' || EMAIL_RE.test(email.trim());

  // ── Flow ──
  const [step, setStep] = useState<'check' | 'confirm' | 'success'>('check');
  // Garde-fou anti-autofill iOS : si le Keychain a tout de même glissé un email
  // dans le champ Nom à l'arrivée sur l'étape Confirm, on le nettoie (~400ms
  // après, pour attraper un remplissage tardif). Le readOnly du champ devrait
  // déjà l'empêcher — ceci est une ceinture-bretelles.
  useEffect(() => {
    if (step !== 'confirm') return;
    const t = setTimeout(() => setName((cur) => (/\S+@\S+/.test(cur) ? '' : cur)), 400);
    return () => clearTimeout(t);
  }, [step]);
  const [submitting, setSubmitting] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const [successInfo, setSuccessInfo] = useState<{ time: string; date: Date; guests: number; name: string } | null>(null);

  // Les 3 sélections (date / heure / couverts) sont INDÉPENDANTES : changer la
  // date ne réinitialise plus l'heure ni les couverts choisis par l'user.
  const onSelectDate = useCallback((d: Date) => {
    setSelectedDate(d);
  }, []);

  const onSelectTime = useCallback((t: string) => {
    setSelectedTime(t);
  }, []);

  // Bloque le scroll body quand le calendrier est ouvert.
  useEffect(() => {
    if (showCalendar) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showCalendar]);

  // Détection « clavier ouvert » via visualViewport : quand le clavier monte, le
  // viewport visible rétrécit. Quand il est ouvert (étape Confirm), le CTA fixe
  // passe EN FLUX (cf. ConfirmStep) → il scrolle avec le contenu et ne recouvre
  // plus AUCUN champ, quelle que soit sa position (bug textarea IMG_6284). On
  // expose aussi --keyboard-inset (utile pour la safe-area / cas in-flow).
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const lastKeyboardHeight = useRef(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handler = () => {
      const delta = window.innerHeight - vv.height;
      setKeyboardOpen(delta > KEYBOARD_MIN_HEIGHT);
      document.documentElement.style.setProperty('--keyboard-inset', `${Math.max(0, delta - vv.offsetTop)}px`);
      // Hauteur de clavier changée (ouverture, ou texte → numérique) : on (re)joue
      // le scroll du champ focus. On compare sur `delta` (indépendant de offsetTop)
      // et avec un seuil, pour ne rien déclencher sur les simples events `scroll`
      // du viewport visible — sinon on volerait le scroll manuel de l'utilisateur.
      if (Math.abs(delta - lastKeyboardHeight.current) > 24) {
        lastKeyboardHeight.current = delta;
        if (delta > KEYBOARD_MIN_HEIGHT) setTimeout(() => scrollFocusedFieldToTop(), 80);
      }
    };
    vv.addEventListener('resize', handler);
    vv.addEventListener('scroll', handler);
    handler();
    return () => {
      vv.removeEventListener('resize', handler);
      vv.removeEventListener('scroll', handler);
      document.documentElement.style.removeProperty('--keyboard-inset');
    };
  }, []);

  const checkAvailability = async () => {
    // La valeur d'heure par défaut (activeTime) est utilisable telle quelle : pas
    // besoin que l'user ait tapé l'heure pour valider le créneau.
    if (!selectedDate || !activeTime || guests == null) return;
    setSubmitting(true);
    setNetworkError(false);
    try {
      // Délai mini de « vérification » (sensation qu'on regarde pour vous) même si
      // le backend répond en ~80ms.
      const [r] = await Promise.all([
        apiFetch(
          `/reservations/disponibilites?date=${ymd(selectedDate)}&heure=${activeTime}&couverts=${guests}&service=${activeService}`,
          { auth: false },
        ),
        new Promise((res) => setTimeout(res, 450)),
      ]);
      const d = await r.json();
      const available = !(d.available === false || d.disponible === false);
      setAvailResult({
        available,
        remaining: typeof d.remaining === 'number' ? d.remaining : (typeof d.couverts_restants === 'number' ? d.couverts_restants : null),
        suggestions: Array.isArray(d.suggestions) ? d.suggestions : [],
      });
      setStep('confirm'); // TOUJOURS : le résultat (dispo OU complet) s'affiche à l'étape 2
    } catch {
      setNetworkError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmReservation = async () => {
    if (!activeTime || !selectedDate || guests == null) return;
    setSubmitting(true);
    setNetworkError(false);
    try {
      const res = await apiFetch(`/reservations`, {
        method: 'POST',
        auth: false,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom: name.trim(),
          telephone: phone.replace(/\s/g, '') || null,
          // Email optionnel et non bloquant : envoyé seulement s'il est valide
          // (un format invalide serait rejeté par le Zod backend → on l'ignore).
          email: emailValid && email.trim() ? email.trim() : null,
          date: ymd(selectedDate),
          heure: activeTime,
          couverts: guests,
          notes: notes.trim() || null,
          service: activeService,
          source: 'app',
          // Pot de miel : toujours vide pour un humain (champ masqué).
          website: hp,
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (res.status === 409) {
        // Capacité dépassée entre le check et le confirm → bascule la carte récap
        // de l'étape 2 en « complet » + alternatives (on reste à l'étape 2).
        setAvailResult({ available: false, remaining: typeof result.remaining === 'number' ? result.remaining : 0, suggestions: Array.isArray(result.suggestions) ? result.suggestions : [] });
        return;
      }
      if (!res.ok) { setNetworkError(true); return; }
      setSuccessInfo({ time: activeTime, date: selectedDate, guests, name: name.trim() });
      setStep('success');
    } catch {
      setNetworkError(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Clic sur une alternative (carte « complet ») → pré-remplit la nouvelle
  // sélection et RAMÈNE à l'étape 1 (l'user voit son choix, peut re-vérifier).
  const applySuggestion = (s: Suggestion) => {
    setSelectedDate(parseYmd(s.date));
    setSelectedTime(s.heure);
    setAvailResult(null);
    setNetworkError(false);
    setStep('check');
  };

  const resetForAnother = () => {
    setStep('check');
    setSelectedTime(null);
    setGuests(null);          // aucune présélection (cohérent avec le 1er paint)
    setNotes('');
    setAvailResult(null);
    setSuccessInfo(null);
    setSelectedDate(null);
  };

  // ── Rendu ── (pas de gate skeleton : rendu immédiat, l'agenda affine en place)
  return (
    <div className="rsv">
      <Header
        onClose={() => navigate('/')}
        onBack={step === 'confirm' ? () => setStep('check') : undefined}
      />

      {step === 'check' && (
        <div className="rsv-step rsv-step-check" key="check">
          <DateStrip
            dates={dateOptions}
            selected={selectedDate}
            onSelect={onSelectDate}
            onOpenCalendar={() => setShowCalendar(true)}
          />

          <section className="rsv-section">
            <h2 className="rsv-section-title">À quelle heure&nbsp;?</h2>
            <TimePicker
              allHours={availableHours}
              minuteStep={minuteStep}
              selectedHour={selectedTime ? displayHour : null}
              selectedMinute={selectedTime ? displayMinute : null}
              defaultHour={displayHour}
              defaultMinute={displayMinute}
              maxMinuteByHour={maxMinuteByHour}
              onHourChange={onHourChange}
              onMinuteChange={onMinuteChange}
            />
          </section>

          <GuestStepper
            value={guests}
            max={MAX_GUESTS}
            onChange={(g) => setGuests(g)}
          />

          {networkError && (
            <ErrorCard onRetry={checkAvailability} />
          )}

          {/* Étape 1 = AUCUN check de capacité visible. La dispo est vérifiée au
              click et le résultat (dispo / complet) s'affiche à l'étape 2. */}
          <div className="rsv-cta">
            <button
              className="rsv-btn"
              onClick={checkAvailability}
              disabled={submitting || !selectedDate || guests == null}
            >
              {submitting ? <span className="rsv-spinner" /> : null}
              {submitting ? 'Vérification…' : 'Vérifier la disponibilité'}
            </button>
          </div>
        </div>
      )}

      {step === 'confirm' && activeTime && selectedDate && guests != null && availResult && (
        availResult.available ? (
          <ConfirmStep
            date={selectedDate}
            time={activeTime}
            service={activeService}
            guests={guests}
            remaining={availResult.remaining}
            name={name} setName={setName}
            phone={phone} setPhone={(v) => setPhone(formatFrPhone(v))}
            email={email} setEmail={setEmail}
            nameValid={nameValid} phoneValid={phoneValid} emailValid={emailValid}
            notes={notes}
            setNotes={setNotes}
            hp={hp} setHp={setHp}
            submitting={submitting}
            networkError={networkError}
            keyboardOpen={keyboardOpen}
            onEdit={() => setStep('check')}
            onConfirm={confirmReservation}
          />
        ) : (
          <UnavailableStep
            date={selectedDate}
            time={activeTime}
            service={activeService}
            guests={guests}
            suggestions={availResult.suggestions}
            onPick={applySuggestion}
            onEdit={() => setStep('check')}
          />
        )
      )}

      {step === 'success' && successInfo && (
        <SuccessView
          info={successInfo}
          onAnother={resetForAnother}
          onHome={() => navigate('/')}
        />
      )}

      {showCalendar && (
        <CalendarPopup
          today={today}
          agenda={agenda}
          onSelect={(d) => { onSelectDate(d); setShowCalendar(false); }}
          onClose={() => setShowCalendar(false)}
        />
      )}
    </div>
  );
}

// ── Header ──
function Header({ onClose, onBack }: { onClose: () => void; onBack?: () => void }) {
  return (
    <div className="rsv-header">
      {onBack ? (
        <button className="rsv-header-back" onClick={onBack} aria-label="Retour">
          <MaskIcon name="chevron-left" size={22} color="var(--color-primary)" />
        </button>
      ) : <span className="rsv-header-spacer" />}
      <h1>Réserver une table</h1>
      <button className="rsv-header-close" onClick={onClose}>Fermer</button>
    </div>
  );
}

// ── Date strip (stable) ──
function DateStrip({ dates, selected, onSelect, onOpenCalendar }: {
  dates: Date[];
  selected: Date | null;
  onSelect: (d: Date) => void;
  onOpenCalendar: () => void;
}) {
  const stripRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const selectedKey = selected ? ymd(selected) : null;

  // Scroll fluide vers la puce sélectionnée SANS la déplacer (block:nearest →
  // pas de saut vertical, inline:center → la centre horizontalement si possible).
  useEffect(() => {
    if (!selectedKey) return;
    const el = chipRefs.current[selectedKey];
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [selectedKey]);

  return (
    <section className="rsv-section">
      <h2 className="rsv-section-title">Quand souhaitez-vous venir&nbsp;?</h2>
      <div className="rsv-strip-wrap">
        <div className="rsv-strip" ref={stripRef}>
          <button className="rsv-date-chip rsv-cal-chip" onClick={onOpenCalendar} aria-label="Ouvrir le calendrier">
            <MaskIcon name="calendar" size={24} color="var(--color-primary)" />
          </button>
          {dates.map((d) => {
            const key = ymd(d);
            const active = key === selectedKey;
            return (
              <button
                key={key}
                ref={(el) => { chipRefs.current[key] = el; }}
                className={`rsv-date-chip ${active ? 'active' : ''}`}
                onClick={() => onSelect(d)}
              >
                <span className="rsv-date-dow">{DAYS_FR[d.getDay()]}</span>
                <span className="rsv-date-num">{d.getDate()}</span>
                <span className="rsv-date-mon">{MONTHS_FR[d.getMonth()]}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ── Couverts ── (aucun check capacité ici : pas de warn « complet » à l'étape 1)
function GuestStepper({ value, max, onChange }: {
  value: number | null;
  max: number;
  onChange: (g: number) => void;
}) {
  return (
    <section className="rsv-section">
      <h2 className="rsv-section-title">Combien de convives&nbsp;?</h2>
      <div className="rsv-guests">
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            className={`rsv-guest-chip ${value === n ? 'active' : ''}`}
            onClick={() => onChange(n)}
          >
            {n}
          </button>
        ))}
      </div>
      {/* Info groupe visible par défaut, dès le 1er paint (pas conditionnelle au clic sur 6). */}
      <p className="rsv-guests-hint">Pour un groupe plus important, contactez-nous directement</p>
    </section>
  );
}

// ── Étape 2, cas COMPLET : récap rouge + alternatives cliquables ──
function UnavailableStep({ date, time, service, guests, suggestions, onPick, onEdit }: {
  date: Date; time: string; service: ServiceKey | null; guests: number;
  suggestions: Suggestion[];
  onPick: (s: Suggestion) => void;
  onEdit: () => void;
}) {
  const dateLabel = `${DAYS_FULL_FR[date.getDay()]} ${date.getDate()} ${MONTHS_FR[date.getMonth()]} ${date.getFullYear()}`;
  const serviceLabel = service === 'midi' ? 'Déjeuner' : 'Dîner';
  return (
    <div className="rsv-step rsv-step-confirm" key="unavailable">
      <div className="rsv-step2-enter">
        {/* Carte UNIQUE : ✗ + « Ce service est complet » + le créneau demandé en
            dimmed. Pas de header ni de message dupliqué. */}
        <section className="rsv-section">
          <div className="rsv-recap rsv-recap-full">
            <div className="rsv-recap-topbar">
              <MaskIcon name="xmark" size={26} color="var(--color-error)" />
              <button className="rsv-recap-edit" onClick={onEdit}>Modifier</button>
            </div>
            <p className="rsv-recap-status-full">Ce service est complet</p>
            <div className="rsv-recap-dim">
              <RecapRow icon="calendar" label={dateLabel} />
              <RecapRow icon="clock" label={`${fmtTime(time)} · ${serviceLabel}`} />
              <RecapRow icon="user" label={`${guests} convive${guests > 1 ? 's' : ''}`} />
            </div>
          </div>
        </section>

        <section className="rsv-section">
          {suggestions.length > 0 ? (
            <>
              <h2 className="rsv-section-title">Alternatives</h2>
              <div className="rsv-alt-chips">
                {suggestions.map((s, i) => (
                  <button key={i} className="rsv-alt-chip" onClick={() => onPick(s)}>
                    <span className="rsv-alt-chip-day">{altLabel(s)}</span>
                    <span className="rsv-alt-chip-time">{fmtTime(s.heure)}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="rsv-hint">Aucune disponibilité proche. Essayez une autre date ou appelez-nous directement.</p>
          )}
        </section>
      </div>
    </div>
  );
}

function altLabel(s: Suggestion): string {
  if (s.kind === 'lendemain') return 'Demain';
  if (s.kind === 'autre_service') return s.service === 'midi' ? 'Déjeuner' : 'Dîner';
  const d = parseYmd(s.date);
  return `${DAYS_FR[d.getDay()]} ${d.getDate()}`;
}

// ── Champs contact ──
function ContactFields({ name, setName, phone, setPhone, email, setEmail, nameValid, emailValid, hp, setHp }: {
  name: string; setName: (v: string) => void;
  phone: string; setPhone: (v: string) => void;
  email: string; setEmail: (v: string) => void;
  nameValid: boolean; emailValid: boolean;
  hp: string; setHp: (v: string) => void;
}) {
  // Anti-autofill iOS Keychain (l'email se faufilait dans le champ Nom). Pas
  // d'auto-focus programmatique sur le Nom : un champ texte focus au mount invite
  // le Keychain à remplir. Le Nom reste `readOnly` jusqu'à la 1re interaction
  // réelle (pointerdown) → iOS ne peut pas le pré-remplir ; au tap on lève la garde.
  const [nameReadOnly, setNameReadOnly] = useState(true);

  return (
    <section className="rsv-section">
      <h2 className="rsv-section-title">Vos coordonnées</h2>
      {/* Pot de miel anti-bot : masqué à l'écran et aux lecteurs d'écran, hors
          tabulation. Un humain ne le voit ni ne le remplit ; un bot souvent si. */}
      <input
        type="text"
        name="website"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      />
      <div className="rsv-field">
        <label className="rsv-field-label">Nom <span className="rsv-req">*</span></label>
        <input
          className="rsv-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Votre nom"
          name="reservation-customer-name-input"
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="words"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          readOnly={nameReadOnly}
          onPointerDown={() => setNameReadOnly(false)}
          data-kb-scroll="self"
          onFocus={() => { setNameReadOnly(false); onFieldFocus(); }}
        />
      </div>
      <div className="rsv-field-row">
        <div className="rsv-field">
          <label className="rsv-field-label">Téléphone <span className="rsv-req">*</span></label>
          <input
            className="rsv-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onFocus={onFieldFocus}
            data-kb-scroll="self"
            placeholder="06 12 34 56 78"
            name="reservation-customer-phone-input"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            data-form-type="other"
            data-lpignore="true"
          />
        </div>
        <div className="rsv-field">
          <label className="rsv-field-label">Email <span className="rsv-opt">(optionnel)</span></label>
          <input
            className={`rsv-input ${email.trim() !== '' && !emailValid ? 'invalid' : ''}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={onFieldFocus}
            data-kb-scroll="self"
            placeholder="client@email.com"
            name="reservation-customer-email-input"
            type="email"
            inputMode="email"
            autoComplete="off"
            autoCapitalize="off"
            data-form-type="other"
            data-lpignore="true"
          />
        </div>
      </div>
      {email.trim() !== '' && !emailValid && <p className="rsv-field-err">Format d'email invalide</p>}
      {email.trim() === '' && <p className="rsv-hint">L'email permet de recevoir une confirmation.</p>}
    </section>
  );
}

// ── Étape confirmation (récap + contact) ──
function ConfirmStep({ date, time, service, guests, remaining, name, setName, phone, setPhone, email, setEmail, nameValid, phoneValid, emailValid, notes, setNotes, hp, setHp, submitting, networkError, keyboardOpen, onEdit, onConfirm }: {
  date: Date; time: string; service: ServiceKey | null; guests: number; remaining: number | null;
  name: string; setName: (v: string) => void;
  phone: string; setPhone: (v: string) => void;
  email: string; setEmail: (v: string) => void;
  nameValid: boolean; phoneValid: boolean; emailValid: boolean;
  notes: string; setNotes: (v: string) => void;
  hp: string; setHp: (v: string) => void;
  submitting: boolean; networkError: boolean; keyboardOpen: boolean;
  onEdit: () => void; onConfirm: () => void;
}) {
  // Récap étape 2 : jour en toutes lettres (« Lundi 22 juin »). Les chips de
  // l'étape 1 restent abrégées pour tenir en largeur.
  const dateLabel = `${DAYS_FULL_FR[date.getDay()]} ${date.getDate()} ${MONTHS_FR[date.getMonth()]} ${date.getFullYear()}`;
  const serviceLabel = service === 'midi' ? 'Déjeuner' : 'Dîner';
  // Nom ET téléphone obligatoires. Email optionnel (non bloquant).
  const canConfirm = nameValid && phoneValid;
  return (
    // Racine SANS transform (le CTA fixe enfant ne doit pas être re-ancré).
    <div className="rsv-step rsv-step-confirm" key="confirm">
      {/* Contenu animé à l'arrivée (fade + slide léger). Ce wrapper NE contient PAS
          le CTA fixe → son `transform` d'entrée ne re-ancre pas le bouton. */}
      <div className="rsv-step2-enter">
        {/* Carte UNIQUE (pas de header répété) : la bordure verte + le ✓ + « Il reste
            X places » suffisent à signaler le statut « service disponible ». */}
        <section className="rsv-section">
          <div className="rsv-recap rsv-recap-ok">
            <div className="rsv-recap-topbar">
              <MaskIcon name="check" size={26} color="var(--color-success)" />
              <button className="rsv-recap-edit" onClick={onEdit}>Modifier</button>
            </div>
            <RecapRow icon="calendar" label={dateLabel} />
            <RecapRow icon="clock" label={`${fmtTime(time)} · ${serviceLabel}`} />
            <RecapRow icon="user" label={`${guests} convive${guests > 1 ? 's' : ''}`} />
          </div>
        </section>

        {/* Contact demandé UNIQUEMENT ici, après vérif dispo OK. */}
        <ContactFields
          name={name} setName={setName}
          phone={phone} setPhone={setPhone}
          email={email} setEmail={setEmail}
          nameValid={nameValid} emailValid={emailValid}
          hp={hp} setHp={setHp}
        />

        <section className="rsv-section">
          <h2 className="rsv-section-title">Une demande particulière&nbsp;?</h2>
          <textarea
            className="rsv-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onFocus={onFieldFocus}
            data-kb-scroll="self"
            placeholder="Allergies, occasion, table préférée…"
            rows={4}
            autoComplete="off"
          />
        </section>

        {networkError && <ErrorCard onRetry={onConfirm} />}
      </div>

      {/* CTA ancré en bas (même emplacement que « Vérifier » étape 1), MAIS quand
          le clavier est ouvert il passe EN FLUX (position: static) pour ne plus
          recouvrir les champs Téléphone/Email (bug IMG_6284). Le hint reste
          au-dessus du bouton. */}
      <div className={keyboardOpen ? 'rsv-cta-inflow' : 'rsv-cta'}>
        {!canConfirm && <p className="rsv-cta-hint rsv-cta-hint-above">Renseignez votre nom et votre téléphone pour confirmer</p>}
        <button className="rsv-btn" onClick={onConfirm} disabled={submitting || !canConfirm}>
          {submitting ? <span className="rsv-spinner" /> : null}
          {submitting ? 'Confirmation…' : 'Confirmer la réservation'}
        </button>
      </div>
    </div>
  );
}

function RecapRow({ icon, label }: { icon: string; label: string }) {
  return (
    <div className="rsv-recap-row">
      <MaskIcon name={icon} size={18} color="var(--color-primary)" />
      <span>{label}</span>
    </div>
  );
}

// ── Succès ──
function SuccessView({ info, onAnother, onHome }: {
  info: { time: string; date: Date; guests: number; name: string };
  onAnother: () => void;
  onHome: () => void;
}) {
  const { branding } = useBranding();
  const dateLabel = `${DAYS_FR[info.date.getDay()]} ${info.date.getDate()} ${MONTHS_FR[info.date.getMonth()]}`;
  return (
    <div className="rsv-step rsv-success" key="success">
      <div className="rsv-success-check">
        <svg viewBox="0 0 52 52" className="rsv-check-svg">
          <circle className="rsv-check-circle" cx="26" cy="26" r="24" fill="none" />
          <path className="rsv-check-mark" fill="none" d="M14 27l8 8 16-18" />
        </svg>
      </div>
      <h2 className="rsv-success-title">C'est réservé&nbsp;!</h2>
      <p className="rsv-success-sub">
        {info.name ? `${info.name}, votre` : 'Votre'} table pour {info.guests} est confirmée<br />
        <strong>{dateLabel} à {fmtTime(info.time)}</strong>
      </p>
      <p className="rsv-success-note">À très bientôt au {branding.displayName}.</p>
      <div className="rsv-success-actions">
        <button className="rsv-btn" onClick={onHome}>Terminé</button>
        <button className="rsv-btn-ghost" onClick={onAnother}>Faire une autre réservation</button>
      </div>
    </div>
  );
}

// ── Erreur réseau ──
function ErrorCard({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rsv-error">
      <span>Connexion interrompue. Vérifiez votre réseau.</span>
      <button onClick={onRetry}>Réessayer</button>
    </div>
  );
}
